/**
 * DemoLanding.js — Sprint 1 Workstream C (v6.32.146)
 *
 * Public marketing landing page at /demo. Renders the 2 product cards
 * (MOSAIC Practice + MOSAIC Summative), the 3 secondary tiles
 * (Screener, Sample BLM download, Schedule walkthrough), the trust
 * strip, and the persistent WIDA® independence disclaimer.
 *
 * Strict design contract: /app/design_guidelines.md.
 *   • Trust Blue #00558C primary
 *   • Indigo for Practice, Emerald for Summative
 *   • Merriweather (serif) headings; IBM Plex Sans body
 *
 * The Sample BLM modal posts to /api/demo/sample-blm/request which
 * stores a lead, fires SendGrid (best-effort), and returns the signed
 * download URL inline.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Timer, ClipboardList, Download, CalendarClock, ArrowRight,
  ShieldCheck, CheckCircle2, AlertTriangle, Loader2, Mail,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUST_STATS = [
  { num: "5", label: "ELD content areas" },
  { num: "112", label: "Print-ready booklets" },
  { num: "v1", label: "Audit-grade watermark" },
  { num: "K-12", label: "Full grade coverage" },
];

export default function DemoLanding() {
  const navigate = useNavigate();
  const [sampleOpen, setSampleOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    axios.get(`${API}/demo/sample-blm/catalog`)
      .then((r) => setCatalog(r.data?.samples || []))
      .catch(() => setCatalog([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="demo-landing-root">
      {/* Site chrome (ChromeShell) provides the top nav. */}

      {/* Hero */}
      <section className="bg-[#FAFAFA] py-20 sm:py-24" data-testid="demo-hero">
        <div className="max-w-6xl mx-auto px-6">
          <div className="font-mono text-xs uppercase tracking-[0.18em] text-[#00558C] mb-4">
            English Language Proficiency Practice for Grades K&ndash;12
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight max-w-4xl">
            Assessment built for <span className="text-[#00558C]">multilingual learners</span> —
            print and digital, in lockstep.
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mt-6 leading-relaxed">
            A research-aligned platform for K-12 English language development.
            Watermarked, audit-grade booklets for the print shop. Mirror-identical
            digital experience for the classroom. Both shipped from the same
            source of truth — so what students see on paper is what they see on
            screen, to the exact item.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-slate-500">
            <span>5 ELD Standards · 4 Key Language Uses · 6 Proficiency Levels</span>
            <span className="text-slate-400">·</span>
            <span>Title III-ready screener</span>
            <span className="text-slate-400">·</span>
            <span>Independent — not affiliated with WIDA®</span>
          </div>
        </div>
      </section>

      {/* Product cards */}
      <section className="py-16 bg-white border-y border-slate-200" data-testid="demo-products">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ProductCard
            tone="practice"
            badge="● Modality Practice"
            title="MOSAIC Practice"
            descriptor={<>Daily and weekly language-development practice across Listening, Reading, Writing, and Speaking. Culturally responsive Big Picture contexts. <span className="text-slate-500 italic">Designed around Listening, Speaking, Reading, and Writing, with language-development tasks and key language uses.</span></>}
            features={[
              "1,367 practice items across the 5 ELD content areas",
              "114 culturally-responsive Big Picture contexts",
              "Watermarked, auditable print-ready booklets",
              "Class management · live reports · parent letters in 8 languages",
            ]}
            ctaLabel="Try Practice — 3 min demo"
            ctaTestId="demo-cta-try-practice"
            onCta={() => navigate("/try-demo")}
            timer="~3 minutes"
            timerNote="No login required"
          />
          <ProductCard
            tone="summative"
            badge="● Cross-Curricular Summative"
            title="MOSAIC Summative"
            descriptor={<>Cross-curricular summative assessment for K-12 multilingual learners. Tiered delivery with stop-at-ceiling logic. <span className="text-slate-500 italic">Item design and reporting use MOSAIC's independent proficiency rubric and reporting approach.</span></>}
            features={[
              "Strict coverage: Language Arts · Math · Science · Social Studies",
              "Six grade clusters: K · 1 · 2-3 · 4-5 · 6-8 · 9-12",
              "Tiered A/B/C with test-until-ceiling stopping rule",
              "Individual Student Proficiency Reports (composite + per-domain)",
            ]}
            ctaLabel="Try Summative — 5 min demo"
            ctaTestId="demo-cta-try-summative"
            onCta={() => navigate("/program/switch")}
            timer="~5 minutes"
            timerNote="Coming Q3 — priority access available"
            ctaDisabled={false}
          />
        </div>
      </section>

      {/* Secondary tiles */}
      <section className="py-16 bg-[#FAFAFA]" data-testid="demo-secondary-tiles">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <SecondaryTile
            icon={ClipboardList}
            title="MOSAIC Screener — 2 min"
            body="Title III-compliant identification check for newly enrolled multilingual students. Returns a proficiency placement estimate."
            href="/try-demo"
            testId="demo-tile-screener"
          />
          <SecondaryTile
            icon={Download}
            title="Download a sample booklet"
            body="Get a watermarked PDF of one full grade-cluster assessment. Email-gated. No sales call required."
            onClick={() => setSampleOpen(true)}
            testId="demo-tile-sample-blm"
          />
          <SecondaryTile
            icon={CalendarClock}
            title="Schedule a 15-min walkthrough"
            body="A district administrator walks you through pricing, scope, deployment timelines, and procurement options."
            href="/request-demo"
            testId="demo-tile-schedule"
          />
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-white border-y border-slate-200 py-10" data-testid="demo-trust-strip">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {TRUST_STATS.map((s) => (
            <div key={s.label} data-testid={`demo-trust-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="font-serif text-3xl text-[#00558C] font-bold">{s.num}</div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* v6.38.1 — Local inline Footer removed. The canonical site footer
          (components/layout/Footer.js) is rendered site-wide by ChromeShell
          in App.js and carries the approved WIDA® trademark + non-affiliation
          disclosure. Pre-fix /demo rendered two stacked footers. */}

      {/* Sample BLM modal */}
      <SampleBLMDialog
        open={sampleOpen}
        onOpenChange={setSampleOpen}
        catalog={catalog}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* sub-components                                                       */
/* -------------------------------------------------------------------- */

function ProductCard({ tone, badge, title, descriptor, features, ctaLabel, ctaTestId, onCta, timer, timerNote, ctaDisabled }) {
  const isPractice = tone === "practice";
  const accent = isPractice ? "text-[#1E3A8A]" : "text-[#059669]";
  const bgAccent = isPractice ? "bg-[#EFF6FF]" : "bg-[#ECFDF5]";
  const btnAccent = isPractice
    ? "bg-[#1E3A8A] hover:bg-[#172B6B] text-white"
    : "bg-[#059669] hover:bg-[#047857] text-white";
  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl p-8 shadow-sm flex flex-col gap-5`}
      data-testid={`demo-product-card-${tone}`}
    >
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${accent} ${bgAccent} px-3 py-1 rounded-full w-fit`}>
        {badge}
      </span>
      <h2 className={`font-serif text-3xl font-bold ${accent}`}>{title}</h2>
      <p className="text-[15px] leading-relaxed text-slate-700">{descriptor}</p>
      <ul className="space-y-2.5 text-sm text-slate-700">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${accent}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={onCta}
        disabled={ctaDisabled}
        data-testid={ctaTestId}
        className={`w-full justify-center ${btnAccent} disabled:opacity-60`}
      >
        {ctaLabel} <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
      <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
        <span className="inline-flex items-center gap-1.5"><Timer className="w-3.5 h-3.5" /> {timer}</span>
        <span>{timerNote}</span>
      </div>
    </div>
  );
}

function SecondaryTile({ icon: Icon, title, body, href, onClick, testId }) {
  const inner = (
    <div className="flex gap-4 items-start">
      <div className="w-10 h-10 rounded-lg bg-slate-100 text-[#00558C] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h4 className="font-semibold text-[15px] mb-1">{title}</h4>
        <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
  const klass = "block bg-white border border-slate-200 hover:border-[#00558C] hover:shadow-sm transition-all rounded-xl p-6 text-left w-full";
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={klass} data-testid={testId}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={href || "#"} className={klass} data-testid={testId}>
      {inner}
    </Link>
  );
}

function SampleBLMDialog({ open, onOpenChange, catalog }) {
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", role: "", district: "",
    filename: "", consent: true,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Set default filename when catalog loads
  useEffect(() => {
    if (catalog.length && !form.filename) {
      setForm((f) => ({ ...f, filename: catalog[0].filename }));
    }
  }, [catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setError("You must consent to download.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/demo/sample-blm/request`, form);
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setForm({
      email: "", first_name: "", last_name: "", role: "", district: "",
      filename: catalog[0]?.filename || "", consent: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md" data-testid="demo-sample-blm-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Download a sample booklet</DialogTitle>
              <DialogDescription className="text-slate-600">
                We'll email you the link and also show it here. Good for 7 days.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="demo-sample-blm-form">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="First name" id="first_name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required />
                <FormField label="Last name" id="last_name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required />
              </div>
              <FormField label="Work email" id="email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Role (optional)" id="role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} placeholder="Teacher, Principal…" />
                <FormField label="District (optional)" id="district" value={form.district} onChange={(v) => setForm({ ...form, district: v })} />
              </div>
              <div>
                <Label htmlFor="filename" className="text-xs uppercase tracking-wider text-slate-500">Sample</Label>
                <select
                  id="filename"
                  value={form.filename}
                  onChange={(e) => setForm({ ...form, filename: e.target.value })}
                  className="w-full mt-1 bg-white border border-slate-300 rounded-md px-3 py-2 text-sm"
                  data-testid="demo-sample-filename-select"
                >
                  {catalog.map((s) => (
                    <option key={s.filename} value={s.filename}>{s.label}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-600 mt-2">
                <input
                  type="checkbox"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  data-testid="demo-sample-consent"
                  className="mt-0.5"
                />
                <span>I consent to MOSAIC contacting me about this sample and related products. (No spam — one email per follow-up.)</span>
              </label>
              {error && (
                <div className="text-rose-700 text-sm flex items-center gap-2" data-testid="demo-sample-error">
                  <AlertTriangle className="w-4 h-4" /> {error}
                </div>
              )}
              <DialogFooter className="pt-2">
                <Button type="submit" disabled={busy} className="bg-[#00558C] hover:bg-[#003F6A] text-white w-full sm:w-auto" data-testid="demo-sample-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Email me the sample
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div data-testid="demo-sample-blm-success">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
                Your sample is ready
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                {result.email_sent
                  ? "Check your inbox in the next minute. Link also below."
                  : "Email is queued — for now, use the direct link below."}
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Download link (good 7 days)</p>
              <a
                href={result.download_url}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-xs text-[#00558C] break-all hover:underline"
                data-testid="demo-sample-success-link"
              >
                {result.download_url}
              </a>
            </div>
            <DialogFooter>
              <Button asChild className="bg-[#00558C] hover:bg-[#003F6A] text-white w-full sm:w-auto" data-testid="demo-sample-success-download-btn">
                <a href={result.download_url} target="_blank" rel="noreferrer">
                  <Download className="w-4 h-4 mr-2" /> Download now
                </a>
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, id, value, onChange, type = "text", placeholder, required }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={`demo-sample-input-${id}`}
        className="mt-1"
      />
    </div>
  );
}
